// Android settings (/settings/android, docs/06 §4, native only): the
// "stay reachable" foreground-service toggle (with its doze-exemption prompt),
// the push-transport picker (FCM default, ntfy/UnifiedPush for
// Google-blocked networks), and a recommendation to install the ntfy app so
// UnifiedPush actually has a distributor to hand off to. Bridged to the
// shell's Capacitor seams via nativeSettings.ts — a direct visit on the web
// returns to Settings.

import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useT } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';
import {
  createNativeSettings,
  type NativeSettingsBridge,
  type PushTransport,
} from './nativeSettings.js';
import { buttonClass, Section, SettingsSubScreen } from './chrome.js';

// F-Droid / direct-APK mirrors are a deliberate follow-up (for now, Play only —
// it's the distributor most non-technical users already have an account for).
const NTFY_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=io.heckel.ntfy';

function AndroidSection({ bridge }: { bridge: NativeSettingsBridge }): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const [loaded, setLoaded] = useState(false);
  const [stayReachable, setStayReachableState] = useState(false);
  const [batteryExempt, setBatteryExempt] = useState<boolean | null>(null);
  const [transport, setTransport] = useState<PushTransport>('fcm');
  const [ntfyAvailable, setNtfyAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void bridge
      .load()
      .then((state) => {
        if (cancelled) return;
        setStayReachableState(state.stayReachable);
        setBatteryExempt(state.batteryExempt);
        setTransport(state.pushTransport);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true); // controls stay usable with defaults
      });
    return () => {
      cancelled = true;
    };
  }, [bridge]);

  // The doze-exemption dialog answers off-screen (system UI); re-read the
  // state whenever the app comes back so the note below stays honest.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      void bridge
        .refreshBatteryExempt()
        .then((exempt) => setBatteryExempt(exempt))
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [bridge]);

  async function handleStayReachable(enabled: boolean): Promise<void> {
    setStayReachableState(enabled); // optimistic — the service flip is fire-and-forget UX
    try {
      const { batteryExempt: exempt } = await bridge.setStayReachable(enabled);
      if (enabled) setBatteryExempt(exempt);
    } catch {
      setStayReachableState(!enabled);
    }
  }

  async function handleTransport(next: PushTransport): Promise<void> {
    if (busy || next === transport) return;
    setBusy(true);
    setTransport(next);
    setNtfyAvailable(null);
    try {
      const result = await bridge.setPushTransport(next, (body) => api.updateCurrentSession(body));
      setNtfyAvailable(result.ntfyAvailable);
    } catch {
      // persisted choice still applies on next boot; hint stays neutral
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title={t('android.title')}>
      <label className="flex min-h-touch items-center gap-3 text-sm text-ink">
        <input
          type="checkbox"
          role="switch"
          checked={stayReachable}
          disabled={!loaded}
          onChange={(e) => void handleStayReachable(e.target.checked)}
          className="h-5 w-5"
        />
        {t('android.stayReachable')}
      </label>
      <p className="text-xs text-muted">{t('android.stayReachableHelp')}</p>
      {stayReachable && batteryExempt === false && (
        <p role="status" className="text-xs text-warn">
          {t('android.batteryWarn')}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void bridge.requestBatteryExemption().catch(() => undefined)}
          >
            {t('android.allowBattery')}
          </button>
        </p>
      )}

      <fieldset className="flex flex-col gap-1" disabled={!loaded || busy}>
        <legend className="text-sm text-ink">{t('android.pushTransport')}</legend>
        <label className="flex min-h-touch items-center gap-3 text-sm text-ink">
          <input
            type="radio"
            name="push-transport"
            value="fcm"
            checked={transport === 'fcm'}
            onChange={() => void handleTransport('fcm')}
            className="h-5 w-5"
          />
          {t('android.fcm')}
        </label>
        <label className="flex min-h-touch items-center gap-3 text-sm text-ink">
          <input
            type="radio"
            name="push-transport"
            value="ntfy"
            checked={transport === 'ntfy'}
            onChange={() => void handleTransport('ntfy')}
            className="h-5 w-5"
          />
          {t('android.ntfy')}
        </label>
        <p className="text-xs text-muted">{t('android.ntfyHelp')}</p>
        {transport === 'ntfy' && ntfyAvailable === false && (
          <p role="status" className="text-xs text-warn">
            {t('android.noDistributor')}
          </p>
        )}
      </fieldset>
    </Section>
  );
}

/** Standalone recommendation, independent of the transport radio above: even
 *  on FCM, having a UnifiedPush distributor installed is what makes the ntfy
 *  option (and any future distributor) actually work if Google push is ever
 *  blocked on the user's network. */
function NtfyAppSection(): ReactNode {
  const t = useT();
  return (
    <Section title={t('android.ntfyAppTitle')}>
      <p className="text-xs text-muted">{t('android.ntfyAppHelp')}</p>
      <p className="text-xs text-muted">{t('android.ntfyAppTrust')}</p>
      <a
        href={NTFY_PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center ${buttonClass}`}
      >
        {t('android.ntfyAppGet')}
      </a>
    </Section>
  );
}

export function AndroidSettingsScreen({
  nativeSettings,
}: {
  /** Test seam; defaults to the real Capacitor probe + dynamic import. */
  nativeSettings?: NativeSettingsBridge;
} = {}): ReactNode {
  const t = useT();
  const [bridge] = useState<NativeSettingsBridge>(
    () => nativeSettings ?? createNativeSettings(),
  );
  // Native-only screen: a direct visit on the web returns to Settings.
  if (!bridge.isNative()) return <Navigate to="/settings" replace />;
  return (
    <SettingsSubScreen section="android" title={t('android.title')}>
      <AndroidSection bridge={bridge} />
      <NtfyAppSection />
    </SettingsSubScreen>
  );
}
